import React from "react";
import { useLocation } from "react-router-dom";

const topPadding = "pt-14 sm:pt-16 md:pt-[72px] lg:pt-24";

const SkeletonLine: React.FC<{ className: string }> = ({ className }) => (
  <div className={`rounded-md bg-white/70 ${className}`} />
);

const SkeletonCard: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div
    className={`rounded-card bg-white/70 border border-border-light shadow-card ${className}`.trim()}
  />
);

const HomeSkeleton: React.FC = () => {
  return (
    <div className={topPadding}>
      <div className="animate-pulse">
        {/* Hero full-bleed like real UI */}
        <section className="relative overflow-hidden bg-[#EFE5D5]">
          <div className="absolute inset-0 bg-white/30" />
          <div className="relative h-[400px] sm:h-[520px] md:h-[600px] lg:h-[680px] xl:h-[750px] 2xl:h-[820px]">
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-6 pb-3 sm:py-10 lg:py-20 h-full flex items-start lg:items-center">
              <div className="w-full max-w-4xl lg:max-w-none space-y-4">
                <SkeletonLine className="h-10 sm:h-14 lg:h-16 w-[92%]" />
                <SkeletonLine className="h-4 sm:h-5 w-[86%] bg-white/60" />
                <SkeletonLine className="h-4 sm:h-5 w-[70%] bg-white/60" />
                <div className="pt-4 flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                  <div className="h-12 w-[60%] mx-auto sm:mx-0 sm:w-48 rounded-button bg-white/70" />
                  <div className="h-12 w-[60%] mx-auto sm:mx-0 sm:w-48 rounded-button bg-white/70" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sections */}
        <section className="bg-background-light py-12 sm:py-14 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-10">
              <div className="text-center space-y-3">
                <SkeletonLine className="h-8 sm:h-10 w-2/3 mx-auto" />
                <SkeletonLine className="h-4 sm:h-5 w-3/4 mx-auto bg-white/60" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <SkeletonCard className="h-[200px] max-w-[350px] mx-auto" />
                <SkeletonCard className="h-[200px] max-w-[350px] mx-auto" />
                <SkeletonCard className="h-[200px] max-w-[350px] mx-auto" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const AboutSkeleton: React.FC = () => {
  return (
    <div className={`${topPadding} min-h-screen`}>
      <div className="animate-pulse">
        <section className="bg-background-light pt-8 pb-5 sm:pt-8 sm:pb-5 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl lg:max-w-none mx-auto space-y-3">
              <SkeletonLine className="h-10 sm:h-12 lg:h-14 w-56 mx-auto" />
              <SkeletonLine className="h-4 sm:h-5 w-3/4 mx-auto bg-white/60" />
            </div>
          </div>
        </section>

        <section className="bg-background-light pt-4 pb-8 sm:pt-4 sm:pb-8 lg:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
              <div className="space-y-5">
                <SkeletonLine className="h-8 sm:h-10 lg:h-12 w-40" />
                <SkeletonLine className="h-4 sm:h-5 w-full bg-white/60" />
                <SkeletonLine className="h-4 sm:h-5 w-[92%] bg-white/60" />
                <SkeletonLine className="h-4 sm:h-5 w-[80%] bg-white/60" />
                <SkeletonLine className="h-6 sm:h-7 lg:h-8 w-48 mt-4" />
                <SkeletonLine className="h-4 sm:h-5 w-[88%] bg-white/60" />
              </div>
              <SkeletonCard className="h-[260px] sm:h-[320px] lg:h-[360px]" />
            </div>
          </div>
        </section>

        <section className="bg-background-white py-10 sm:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              <SkeletonCard className="h-[220px]" />
              <SkeletonCard className="h-[220px]" />
            </div>
          </div>
        </section>

        <section className="bg-background-light py-10 sm:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 space-y-3">
              <SkeletonLine className="h-10 w-56 mx-auto" />
              <SkeletonLine className="h-4 sm:h-5 w-3/4 mx-auto bg-white/60" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <SkeletonCard className="h-[220px]" />
              <SkeletonCard className="h-[220px]" />
              <SkeletonCard className="h-[220px]" />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const WorkSkeleton: React.FC = () => {
  return (
    <div className={`${topPadding} min-h-screen bg-background-light`}>
      <div className="animate-pulse">
        <section className="py-8 sm:py-8 lg:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl lg:max-w-none mx-auto mb-6 sm:mb-6 lg:mb-10 space-y-3">
              <SkeletonLine className="h-10 sm:h-12 lg:h-14 w-72 mx-auto" />
              <SkeletonLine className="h-4 sm:h-5 w-3/4 mx-auto bg-white/60" />
            </div>

            <div className="space-y-6 sm:space-y-6 lg:space-y-14">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
                <div className="space-y-3">
                  <SkeletonLine className="h-8 sm:h-10 lg:h-12 w-80" />
                  <SkeletonLine className="h-5 w-2/3 bg-white/60" />
                  <SkeletonLine className="h-4 sm:h-5 w-full bg-white/60" />
                  <SkeletonLine className="h-4 sm:h-5 w-[92%] bg-white/60" />
                </div>
                <SkeletonCard className="h-[220px] sm:h-[280px] lg:h-[320px]" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
                <div className="space-y-3">
                  <SkeletonLine className="h-8 sm:h-10 lg:h-12 w-72" />
                  <SkeletonLine className="h-5 w-2/3 bg-white/60" />
                  <SkeletonLine className="h-4 sm:h-5 w-full bg-white/60" />
                  <SkeletonLine className="h-4 sm:h-5 w-[88%] bg-white/60" />
                </div>
                <SkeletonCard className="h-[220px] sm:h-[280px] lg:h-[320px]" />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-white py-10 sm:py-12 lg:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 space-y-3">
              <SkeletonLine className="h-10 w-80 mx-auto" />
              <SkeletonLine className="h-4 sm:h-5 w-3/4 mx-auto bg-white/60" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              <SkeletonCard className="h-[190px]" />
              <SkeletonCard className="h-[190px]" />
              <SkeletonCard className="h-[190px]" />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const ContactSkeleton: React.FC = () => {
  return (
    <div className={`${topPadding} min-h-screen bg-background-light`}>
      <div className="animate-pulse">
        <section className="py-10 sm:py-12 lg:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto mb-10 sm:mb-12 space-y-3">
              <SkeletonLine className="h-10 sm:h-12 lg:h-14 w-56 mx-auto" />
              <SkeletonLine className="h-4 sm:h-5 w-3/4 mx-auto bg-white/60" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
              <div className="space-y-5">
                <SkeletonLine className="h-8 sm:h-10 lg:h-12 w-72" />
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/70 border border-border-light" />
                  <SkeletonLine className="h-4 sm:h-5 w-64 bg-white/60" />
                </div>
              </div>
              <SkeletonCard className="h-[520px]" />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const LegalSkeleton: React.FC<{ titleWidthClass: string }> = ({ titleWidthClass }) => {
  return (
    <div className={`${topPadding} min-h-screen`}>
      <div className="animate-pulse">
        <section className="bg-background-light pt-8 pb-5 sm:pt-8 sm:pb-5 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl lg:max-w-none mx-auto space-y-3">
              <SkeletonLine className={`h-10 sm:h-12 lg:h-14 ${titleWidthClass} mx-auto`} />
              <SkeletonLine className="h-4 sm:h-5 w-3/4 mx-auto bg-white/60" />
            </div>
          </div>
        </section>
        <section className="bg-background-light pt-4 pb-8 sm:pt-4 sm:pb-8 lg:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SkeletonCard className="h-[260px]" />
          </div>
        </section>
      </div>
    </div>
  );
};

export const RouteFallback: React.FC = () => {
  const { pathname } = useLocation();

  if (pathname === "/") return <HomeSkeleton />;
  if (pathname.startsWith("/about")) return <AboutSkeleton />;
  if (pathname.startsWith("/work")) return <WorkSkeleton />;
  if (pathname.startsWith("/contact")) return <ContactSkeleton />;
  if (pathname.startsWith("/privacy")) return <LegalSkeleton titleWidthClass="w-56" />;
  if (pathname.startsWith("/terms")) return <LegalSkeleton titleWidthClass="w-64" />;

  return (
    <div className="bg-background-light" role="status" aria-live="polite" aria-busy="true">
      <div className={`${topPadding} max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12`}>
        <div className="animate-pulse space-y-6">
          <SkeletonLine className="h-8 w-2/3" />
          <SkeletonLine className="h-4 w-1/2 bg-white/60" />
          <SkeletonCard className="h-48" />
        </div>
      </div>
    </div>
  );
};

